import { useEffect, useRef, useState } from "react";
import { PageHeader } from "../../components";
import CustomSelect from "../../components/CustomSelect";
import SubButton from "../../components/SubButtons";
import { colorMap, days, periods } from "../../util/Constants";
import AddTimeTable from "../../components/modal/AddTimetable";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { fetchClasses } from "../../features/classes/ClassesSlice";
import { useBranchCode } from "../../util/hooks/useBranch";
import { TimeTableResponse } from "../../types/timeTableType";
import TimeTableApis from "../../api/TimeTableApis";
import { usePrint } from "../../util/hooks/usePrint";

export const transformTimeTable = (data: TimeTableResponse): any => {
  const dayReverseMap: Record<string, string> = {
    MONDAY: "Mon",
    TUESDAY: "Tue",
    WEDNESDAY: "Wed",
    THURSDAY: "Thu",
    FRIDAY: "Fri",
    SATURDAY: "Sat",
    SUNDAY: "Sun",
  };

  const result: any = {};

  Object.keys(data).forEach((backendDay) => {
    const uiDay = dayReverseMap[backendDay];

    if (!uiDay) return;

    result[uiDay] = {};

    data[backendDay].forEach((item) => {
      const periodKey = `Period ${item.periodNumber}`;

      if (item.subjectName) {
        result[uiDay][periodKey] = {
          subject: item.subjectName,
          teacher: item.staffName,
        };
      }
    });
  });

  return result;
};

export default function TimeTable() {
  const tableRef = useRef<HTMLDivElement>(null);
  const [showTable, setShowTable] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{
    day: string;
    period: string;
  } | null>(null);

  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [timetable, setTimetable] = useState<TimeTableResponse>({});

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
  });

  const gridLayout = "grid grid-cols-[100px_repeat(6,1fr)]";

  const { accessibleClassRooms: classes } = useSelector(
    (state: RootState) => state.access,
  );

  const branchCode = useBranchCode();

  const fetchTimeTable = async () => {
    if (!selectedClassId) return;

    try {
      const response = await TimeTableApis.getEventById(selectedClassId);

      if (response?.status === 1 && response.data) {
        const transformed = transformTimeTable(response.data);
        setTimetable(transformed);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectedClass = classes?.find((cls) => cls.classId === selectedClassId);

  const handlePrint = usePrint(tableRef, {
    documentTitle: `Class ${selectedClass?.className || ""} Timetable`,
  });

  return (
    <div className="flex flex-col gap-4 min-h-screen">
      <PageHeader />

      <section className="flex flex-col gap-3">
        <div className="bg-white rounded-lg p-5 max-w-lg flex flex-col gap-4">
          <CustomSelect
            name="class-select"
            label="Class"
            value={selectedClassId ?? ""}
            options={classes?.map((item) => ({
              label: item.className,
              value: item.classId,
            }))}
            onChange={(e) => {
              setSelectedClassId(Number(e.target.value));
              setShowTable(false);
            }}
            placeholder="Please select class to proceed further"
          />

          <div className="flex justify-end gap-2">
            <SubButton
              text="View"
              color="bg-success-700"
              padding="px-6"
              disabled={!selectedClassId}
              onClick={async () => {
                setShowTable(true);
                await fetchTimeTable();
              }}
            />

            {showTable && (
              <SubButton
                text="Print"
                color="bg-primary-500"
                padding="px-6"
                onClick={handlePrint}
              />
            )}
          </div>
        </div>

        {showTable && (
          <div
            ref={tableRef}
            className="bg-white rounded-lg  overflow-hidden max-w-4xl"
          >
            <div className={`${gridLayout}  border-b border-neutral-50`}>
              <div className="py-4 pl-5 text-[13px] text-neutral-300 font-medium">
                Time
              </div>
              {days.map((day) => (
                <div
                  key={day}
                  className={`py-4 text-center text-[13px] text-neutral-300 border-l border-neutral-50
                  ${day === today ? "font-bold" : "font-medium"}
                `}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Period Rows */}
            {periods.map((period, pIndex) => (
              <div
                key={period}
                className={`${gridLayout} ${pIndex !== periods.length - 1 ? "border-b border-neutral-50" : ""}`}
              >
                <div className="py-6 pl-5 text-sm font-medium text-neutral-300 flex items-center">
                  {period}
                </div>

                {/* Day Cells */}
                {days.map((day, dIndex) => {
                  const colorKey = colors[
                    (pIndex + dIndex) % colors.length
                  ] as keyof typeof colorMap;
                  const entry = timetable?.[day]?.[period];

                  return (
                    <div
                      key={`${period}-${day}`}
                      className="p-2 border-l border-neutral-50 flex items-center justify-center"
                    >
                      {entry ? (
                        <FilledCard
                          subject={entry.subject}
                          teacher={entry.teacher}
                          color={colorKey}
                        />
                      ) : (
                        <AddCard
                          color={colorKey}
                          onClick={() => {
                            setSelectedSlot({ day, period });
                            setShowAddModal(true);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </section>
      {showAddModal && selectedSlot && (
        <AddTimeTable
          day={selectedSlot.day}
          period={selectedSlot.period}
          classId={selectedClassId!}
          branchCode={branchCode}
          onClose={() => {
            setShowAddModal(false);
            setSelectedSlot(null);
          }}
          onSuccess={async () => {
            setShowAddModal(false);
            setSelectedSlot(null);
            await fetchTimeTable();
          }}
        />
      )}
    </div>
  );
}

const colors = ["red", "green", "orange", "blue"];

const AddCard = ({
  color,
  onClick,
}: {
  color: keyof typeof colorMap;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-full h-full min-h-[50px] border rounded-md flex items-center justify-center text-[13px] font-medium transition-all duration-200 ${colorMap[color]}`}
    >
      + Add
    </button>
  );
};

const FilledCard = ({
  subject,
  teacher,
  color,
}: {
  subject: string;
  teacher: string;
  color: keyof typeof colorMap;
}) => {
  return (
    <div
      className={`w-full min-h-[50px] border rounded-md px-2 py-1 text-center text-[13px] font-medium flex flex-col justify-center ${colorMap[color]}`}
    >
      <span className="line-clamp-1">{subject}</span>
      <span className="text-[11px] text-neutral-300 line-clamp-1">
        {teacher}
      </span>
    </div>
  );
};
